import { type UserSession } from '@solinkcorp/authentication';
import { getAccessibleAddress } from '@solinkcorp/utils';
import { isWindowHidden } from '@solinkcorp/utils/hooks';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import React, { useEffect, useMemo, useState } from 'react';
import { Listener } from 'src/core/utils/Listener';
import { getCredentials } from 'src/core/video/VideoUtil';
import useDevice from 'src/hooks/devices/useDevice';
import { useAuthDevice } from 'src/hooks/useAuthDevice';
import { usePlayerCommon } from '../hooks/usePlayerCommon';
import { type PlayerProps } from '../types';
import { closeWhepSession, WhepSignalling, WhepSignallingClosed } from './WhepSignalling';

/**
 * The time we wait for the video to start playing before falling back to HLS.
 * The value must be longer than the camera GOP length as WebRTC will wait for an entire GOP in
 * the case where the GOP cache is full on the MediaMTX side.
 * Setting the value to 15 seconds as it's the average segment length, and cameras should not be
 * configured with longer GOPs.
 */
const WEBRTC_PLAYING_TIMEOUT = 15 * 1000;

/**
 * WebRTCPlayer plays video over WebRTC.
 *
 * This needs MediaMTX v0.23.x or higher running on the device, as we connect
 * using [WHEP](https://www.ietf.org/id/draft-murillo-whep-02.html).
 */
const WebRTCPlayer: React.FC<PlayerProps> = (props) => {
  const {
    playbackController,
    playingCamera,
    selectedStream,
    locationIsLocal,
    user,
    isMuted,
    setVideoRef: playerPropsSetVideoRef,
    overrideWhepURL = false,
  } = props;
  const streamId = selectedStream.stream.id;

  const [peerState, setPeerState] = useState('unknown');

  const camera = playingCamera.camera;

  // LocationStore is empty in the standalone SDM app, so the store lookup can miss.
  const device = useDevice(camera.deviceId) ?? playingCamera.device;

  const authLocation = useAuthDevice(device);

  const { authHeaders, authQueryParams } = useMemo(
    () =>
      overrideWhepURL
        ? { authHeaders: {}, authQueryParams: {} }
        : getCredentials(user, {
            type: 'local', // Always choose local, because we can't play WebRTC from the cloud.
            location: authLocation,
          }),
    [user, authLocation, overrideWhepURL],
  );

  const { updatePlayerStatus, setVideoRef, videoRef, playerLog, restartRequiredCount } =
    usePlayerCommon({
      playbackController,
      playerId: playingCamera.playerId,
      type: 'Live',
      playerInfo: `WebRTC (${peerState})`,
      playerPropsSetVideoRef,
    });

  const turnUrl = camera.turnUrl;
  const whepURL = overrideWhepURL
    ? camera.liveUrl
    : getWhepURL(playingCamera, locationIsLocal, streamId, authQueryParams);

  useEffect(() => {
    if (!videoRef.current || !whepURL || !user) {
      return;
    }

    updatePlayerStatus({ state: 'loading' });
    const videoEl = videoRef.current;

    try {
      playerLog.info(`[webrtc] Starting playback ${whepURL}`);

      let iceCandidates = 0;
      const listener = new Listener();

      updatePlayerStatus({ state: 'loading' });

      const signalling = new WhepSignalling(whepURL);

      // This is the equivalent of `requestICEServers` in MediaMTX's player.
      // See https://github.com/bluenviron/mediamtx/blob/main/internal/servers/webrtc/read_index.html#L182.
      const iceServers = getIceServers(turnUrl, user);

      playerLog.debug(
        `Creating peer connection with servers: ${iceServers
          .map(
            (server) => `${server.username}@${server.credential} - ${JSON.stringify(server.urls)}`,
          )
          .join(', ')}`,
      );
      // TODO: The latest MediaMTX sets `sdpSemantics: 'unified-plan'` here.
      const pc = new RTCPeerConnection({ iceServers });

      listener.listenTo(pc, 'negotiationneeded', () => {
        playerLog.warn(`[WebRTCPlayer] Negotiation needed`);
        iceCandidates = 0;

        Promise.resolve()
          .then(async () => {
            const offer = await pc.createOffer();
            // TODO: The MedixMTX player in the latest version modifies the offer here:
            // https://github.com/bluenviron/mediamtx/blob/5a39e07eb7478ce19e6ea1a4a5e03357487504ce/internal/servers/webrtc/read_index.html#L139
            await pc.setLocalDescription(offer);
            playerLog.info('Sending offer');
            const { remoteDescription, sessionId } = await signalling.sendOffer(offer);

            if (pc.signalingState === 'closed') return;
            await pc.setRemoteDescription(remoteDescription);

            const mediaMtxSession = sessionId?.split('-')[0];
            playerLog.info(`MediaMTX [WebRTC] [session ${mediaMtxSession}]`);
          })
          .catch((err) => {
            if (pc.signalingState === 'closed' || err instanceof WhepSignallingClosed) {
              // Do not fallback or log errors if the connection is closed.
              return;
            }

            playerLog.error(`Error initiating WebRTC connection: ${err}`);
            updatePlayerStatus({ state: 'error', error: err });

            playerLog.log(`[WebRTCPlayer] falling back to HLS`);
            /** fallback to HLS player */
            playbackController.setPlayerTypeFallback({
              playerId: playingCamera.playerId,
              playerType: 'HLS',
              error: err,
            });
          });
      });

      listener.listenTo(pc, 'icecandidateerror', (event: RTCPeerConnectionIceErrorEvent) => {
        playerLog.warn(
          `ICE candidate error: ${event.errorText} (${event.errorCode}) ` +
            `url: ${event.url} candidate: ${event.address}:${event.port}`,
        );
      });

      listener.listenTo(pc, 'icecandidate', (event: RTCPeerConnectionIceEvent) => {
        if (event.candidate) {
          playerLog.debug(`ICE candidate: ${event.candidate.candidate}`);
          iceCandidates++;

          if (event.candidate?.candidate) {
            signalling.sendLocalCandidate(event.candidate).catch((err) => {
              if (!(err instanceof WhepSignallingClosed)) {
                playerLog.warn(`ICE candidate send failed: ${err.message}`);
              }
            });
          }
        } else {
          // We found all the ICE candidates.  Time to stream some video!
          playerLog.info(`Found ${iceCandidates} ICE candidates.`);
        }
      });

      listener.listenTo(pc, 'iceconnectionstatechange', () => {
        playerLog.info(`ICE connection state change: ${pc.iceConnectionState}`);

        if (pc.iceConnectionState === 'disconnected') {
          updatePlayerStatus({ state: 'error', error: 'Peer connection disconnected' });
          playbackController.restartPlayer(playingCamera.playerId, 'Peer connection disconnected');
        } else if (pc.iceConnectionState === 'failed') {
          // On Firefox, we see the connection fail sometimes right when we try to
          // start playing video, but restarting usually gets it playing again.
          updatePlayerStatus({ state: 'error', error: 'Peer connection failed' });
          playbackController.restartPlayer(playingCamera.playerId, 'Peer connection failed');
        } else {
          pc.getStats().then((stats) => {
            let peerState: string = pc.iceConnectionState;

            // If we're connected, find the local and remote candidates.
            const values = [...stats.values()];
            const candidatePair = values.find(
              (s) => s.type === 'candidate-pair' && s.state === 'succeeded',
            );

            if (candidatePair) {
              const local = candidateToString(values, candidatePair.localCandidateId);
              const remote = candidateToString(values, candidatePair.remoteCandidateId);
              peerState = `${pc.iceConnectionState} local: ${local} remote: ${remote}`;
              playerLog.info(peerState);
            }

            setPeerState(peerState);
          });
        }
      });

      listener.listenTo(pc, 'track', (event: RTCTrackEvent) => {
        playerLog.info(`${event.streams.length} ${event.track.kind} track is delivered`);
        videoEl.srcObject = event.streams[0];
      });

      pc.addTransceiver('video', { direction: 'sendrecv' });
      pc.addTransceiver('audio', { direction: 'sendrecv' });

      return () => {
        try {
          playerLog.info(`Cleaning up...`);
          listener.stopListening();
          videoEl.srcObject = null;
        } catch (err) {
          console.error('Error cleaning up webrtc: ', err);
        }
        void closeWhepSession(signalling, pc);
      };
    } catch (err) {
      updatePlayerStatus({ state: 'error', error: err });
      return () => undefined;
    }
  }, [
    playerLog,
    updatePlayerStatus,
    videoRef,
    whepURL,
    restartRequiredCount,
    authHeaders,
    playbackController,
    turnUrl,
    playingCamera.playerId,
    user,
  ]);

  useEffect(() => {
    const listener = new Listener();
    // Wait for the connection to be established before checking if the video is playing.
    if (!peerState.includes('connected') || !videoRef.current) return;

    // Video playing must start within WEBRTC_PLAYING_TIMEOUT. If not, we will fall back to HLS and report an Error.
    const playingTimeout = setTimeout(() => {
      if (isWindowHidden()) {
        // If the window is hidden on timeout, we don't want to fall back to HLS.
        return;
      }
      const error = 'Timeout error';
      playerLog.error(`Timing out when playing WebRTC`);
      updatePlayerStatus({ state: 'error', error });

      playerLog.log(`[WebRTCPlayer] falling back to HLS`);
      /** fallback to HLS player */
      playbackController.setPlayerTypeFallback({
        playerId: playingCamera.playerId,
        playerType: 'HLS',
        error,
      });
    }, WEBRTC_PLAYING_TIMEOUT);
    listener.listenTo(videoRef.current, 'playing', () => clearTimeout(playingTimeout));
    return () => {
      listener.stopListening();
      clearTimeout(playingTimeout);
    };
  }, [
    peerState,
    playbackController,
    playerLog,
    playingCamera.playerId,
    updatePlayerStatus,
    videoRef,
  ]);

  return (
    <>
      <img
        style={{ position: 'absolute', zIndex: -1 }}
        src={props.poster}
        alt="keyframe-thumbnail"
        width="100%"
        height="100%"
      />
      <video
        ref={setVideoRef}
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
        autoPlay
        muted={isMuted}
        playsInline
      />
    </>
  );
};

export default React.memo(WebRTCPlayer);

/**
 * Find the URL to use to connect to the device to start a WebRTC session.
 */
export function getWhepURL(
  playingCamera: PlayingCamera,
  locationIsLocal: boolean,
  streamId: string,
  authQueryParams: Record<string, string> | undefined,
): string | undefined {
  const { vmsCameraId, camera } = playingCamera;

  const deviceUrl = getAccessibleAddress(camera, locationIsLocal);
  if (!deviceUrl) {
    return undefined;
  }

  const whepURL = new URL(deviceUrl);
  whepURL.pathname = `/stream/${vmsCameraId}/${streamId}/whep`;
  whepURL.search = `?${new URLSearchParams(authQueryParams)}`;

  return whepURL.toString();
}

export function getIceServers(turnUrl: string | undefined, user: UserSession): RTCIceServer[] {
  const turnCredentials = user.turnCredentials;
  if (!turnCredentials) {
    throw new Error('User is missing TURN credentials');
  }

  if (!turnUrl) {
    throw new Error('Missing TURN URL.');
  }

  return [
    {
      urls: [turnUrl],
      username: turnCredentials.username,
      credential: turnCredentials.credential,
    },
  ];
}

function typeToString(candidateType: string) {
  switch (candidateType) {
    case 'host':
      return 'Local Network';
    case 'srflx':
    case 'prflx':
      return 'STUN';
    case 'relay':
      return 'TURN';
    default:
      return 'Unknown';
  }
}

/**
 * Given stats from RTCPeerConnection.getStats() and a candidate ID, find the
 * candidate and return a string representation of it.
 */
function candidateToString(stats: any[], candidateId: string | undefined): string | undefined {
  if (!candidateId) {
    return undefined;
  }

  const candidate = stats.find((s) => s.id === candidateId);
  if (!candidate) {
    return undefined;
  }

  return `${candidate.candidateType} (${typeToString(candidate.candidateType)}) ${
    candidate.address
  }:${candidate.port} (${candidate.protocol})`;
}
